import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppBar, Screen } from '@/components/layout';
import { ConditionTagInput, type ConditionValue } from '@/components/ConditionTagInput';
import { Button, Card, Chip, EmptyState, ErrorNote, Field, FieldGroup, Input, SectionTitle, Spinner, Textarea } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { todayISO } from '@/lib/format';
import type { ChildSummary, ClassRoomSummary, ConditionTag, DailyNote } from '@/types';

type ConditionMap = Record<string, Record<string, ConditionValue>>;

/** 알림장 작성 — 명세서 §5.3 */
export function DailyNoteEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [classRoomId, setClassRoomId] = useState('');
  const [noteDate, setNoteDate] = useState(todayISO());
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [conditions, setConditions] = useState<ConditionMap>({});
  const [activeChildId, setActiveChildId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const selectedRoom = rooms.data?.find((room) => room.id === classRoomId) ?? rooms.data?.[0];
  const effectiveRoomId = classRoomId || selectedRoom?.id || '';

  const children = useQuery({
    queryKey: ['children', { classRoomId: effectiveRoomId }],
    queryFn: () => apiData<ChildSummary[]>(`/children?classRoomId=${effectiveRoomId}`),
    enabled: Boolean(effectiveRoomId),
    placeholderData: keepPreviousData,
  });

  const tags = useQuery({
    queryKey: ['condition-tags', selectedRoom?.ageBand],
    queryFn: () =>
      apiData<ConditionTag[]>(
        `/daily-notes/condition-tags${selectedRoom?.ageBand ? `?ageBand=${selectedRoom.ageBand}` : ''}`,
      ),
    enabled: Boolean(selectedRoom),
  });

  const existing = useQuery({
    queryKey: ['daily-notes', id],
    queryFn: () => apiData<DailyNote>(`/daily-notes/${id}`),
    enabled: isEdit,
  });

  // 편집 모드에서 값이 도착하면 한 번만 폼에 채운다.
  useMemo(() => {
    const note = existing.data;
    if (!note) return;
    setClassRoomId(note.classRoom?.id ?? '');
    setNoteDate(note.noteDate.slice(0, 10));
    setTitle(note.title ?? '');
    setContent(note.content);
    setConditions(
      note.conditions.reduce<ConditionMap>((acc, condition) => {
        acc[condition.childId] = { ...acc[condition.childId], [condition.tagCode]: condition.value };
        return acc;
      }, {}),
    );
  }, [existing.data]);

  const currentChildId = activeChildId || children.data?.[0]?.id || '';

  const save = useMutation({
    mutationFn: async (status: 'DRAFT' | 'PUBLISHED') => {
      const body = {
        target: 'CLASS' as const,
        classRoomId: effectiveRoomId,
        noteDate,
        title: title.trim() || undefined,
        content: content.trim(),
        status,
        conditions: Object.entries(conditions).flatMap(([childId, byTag]) =>
          Object.entries(byTag)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([tagCode, value]) => ({ childId, tagCode, value })),
        ),
      };

      return isEdit
        ? apiData<DailyNote>(`/daily-notes/${id}`, { method: 'PATCH', body })
        : apiData<DailyNote>('/daily-notes', { method: 'POST', body });
    },
    onSuccess: (note) => {
      void queryClient.invalidateQueries({ queryKey: ['daily-notes'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
      navigate(note.status === 'PUBLISHED' ? `/notes/${note.id}` : '/notes', { replace: true });
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.');
    },
  });

  function setCondition(childId: string, tagCode: string, value: ConditionValue) {
    setConditions((prev) => ({
      ...prev,
      [childId]: { ...prev[childId], [tagCode]: value },
    }));
  }

  if (rooms.isPending) return <Spinner />;
  if (rooms.isError) {
    return (
      <Screen withNav={false}>
        <AppBar title="알림장 쓰기" back />
        <ErrorNote message="반 정보를 불러오지 못했어요." onRetry={() => void rooms.refetch()} />
      </Screen>
    );
  }
  // 반이 없으면 children 쿼리가 비활성인 채 isPending=true — 폼 대신 이유를 보여 준다.
  if (rooms.data.length === 0) {
    return (
      <Screen withNav={false}>
        <AppBar title="알림장 쓰기" back />
        <EmptyState title="아직 반이 없어요" body="운영 화면에서 반을 만들면 알림장을 쓸 수 있어요." />
      </Screen>
    );
  }

  return (
    <Screen withNav={false}>
      <AppBar title={isEdit ? '알림장 수정' : '알림장 쓰기'} back />

      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          save.mutate('PUBLISHED');
        }}
        className="flex flex-col gap-4 p-4"
      >
        <Card className="flex flex-col gap-4">
          <FieldGroup label="반" required>
            <div className="flex flex-wrap gap-1.5">
              {rooms.data?.map((room) => (
                <Chip
                  key={room.id}
                  tone={room.id === effectiveRoomId ? 'primary' : 'neutral'}
                  selected={room.id === effectiveRoomId}
                  onClick={() => {
                    setClassRoomId(room.id);
                    setActiveChildId('');
                  }}
                >
                  {room.name}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <Field label="날짜" required>
            <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} required />
          </Field>

          <Field label="제목 (선택)">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="오늘의 우리 반" />
          </Field>

          <Field label="오늘 이야기" required>
            <Textarea
              rows={7}
              required
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="아이들이 어떤 하루를 보냈는지 적어 주세요."
            />
          </Field>

        </Card>

        <section>
          <SectionTitle>아이별 컨디션</SectionTitle>

          {children.isPending && <Spinner />}
          {children.data && children.data.length === 0 && (
            <Card>
              <p className="text-base text-ink-sub">이 반에 편성된 원아가 아직 없어요.</p>
            </Card>
          )}

          {children.data && children.data.length > 0 && (
            <Card>
              <div className="scroll-x -mx-1 mb-3">
                <div className="flex w-max gap-1.5 px-1">
                  {children.data.map((child) => {
                    const filled = Object.values(conditions[child.id] ?? {}).filter(
                      (v) => v !== undefined && v !== null,
                    ).length;
                    return (
                      <Chip
                        key={child.id}
                        tone={child.id === currentChildId ? 'primary' : 'neutral'}
                        selected={child.id === currentChildId}
                        onClick={() => setActiveChildId(child.id)}
                      >
                        {child.name}
                        {filled > 0 && <span className="tnum ml-1 opacity-70">{filled}</span>}
                      </Chip>
                    );
                  })}
                </div>
              </div>

              {tags.data?.map((tag) => (
                <ConditionTagInput
                  key={tag.code}
                  tag={tag}
                  value={conditions[currentChildId]?.[tag.code]}
                  onChange={(value) => setCondition(currentChildId, tag.code, value)}
                />
              ))}
            </Card>
          )}
        </section>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <div className="flex gap-2 pb-6">
          <Button
            type="button"
            variant="outline"
            block
            loading={save.isPending}
            onClick={() => save.mutate('DRAFT')}
          >
            임시저장
          </Button>
          <Button type="submit" block loading={save.isPending} disabled={!content.trim()}>
            발행하기
          </Button>
        </div>
      </form>
    </Screen>
  );
}
